import { uploadRecording } from './stream-recording-upload.js';

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

function showToast(message, type = 'info') {
  if (typeof window.showSiteToast === 'function') {
    window.showSiteToast(message, type);
    return;
  }

  alert(message);
}

function formatRecordingDuration(seconds) {
  const total = Math.max(1, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;

  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function formatRecordingSize(bytes) {
  const size = Math.max(0, Number(bytes) || 0);
  if (size >= 1048576) {
    return `${(size / 1048576).toFixed(1)} MB`;
  }

  if (size >= 1024) {
    return `${Math.round(size / 1024)} KB`;
  }

  return `${size} B`;
}

function getVodCardMeta(videoId, page) {
  const card = document.querySelector(`.gv-media-card[data-video-id="${CSS.escape(videoId)}"]`);

  return {
    streamTitle: card?.dataset.videoTitle?.trim() || '',
    durationSeconds: Number(card?.dataset.videoDuration || 0) || 0,
    gameId: page?.dataset.gameId?.trim() || '',
    gameUuid: page?.dataset.game?.trim() || '',
  };
}

function isDirectMp4Source(streamType, streamUrl) {
  const type = String(streamType || '').toLowerCase();
  const url = String(streamUrl || '');

  return type === 'mp4' || (!url.includes('.m3u8') && /\.mp4(?:\?|$)/i.test(url));
}

async function fetchStreamSource(streamApi, videoId) {
  const resp = await fetch(`${streamApi}?video_id=${encodeURIComponent(videoId)}`, {
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });
  const json = await resp.json();

  if (!resp.ok || !json.success) {
    throw new Error(json.error || 'خطا در دریافت لینک ویدیو');
  }

  return json.data || {};
}

async function downloadBlobWithProgress(url, onProgress) {
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error('دانلود مستقیم ویدیو ناموفق بود.');
  }

  const total = Number(resp.headers.get('Content-Length')) || 0;
  if (!resp.body || typeof resp.body.getReader !== 'function') {
    const blob = await resp.blob();
    onProgress?.(100);
    return blob;
  }

  const reader = resp.body.getReader();
  const chunks = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    chunks.push(value);
    loaded += value.byteLength;

    if (total > 0 && typeof onProgress === 'function') {
      onProgress(Math.min(100, Math.round((loaded / total) * 100)));
    }
  }

  onProgress?.(100);

  const mimeType = resp.headers.get('Content-Type') || 'video/mp4';
  return new Blob(chunks, { type: mimeType });
}

async function importVodOnServer({
  importUrl,
  videoId,
  page,
  meta,
  onProgress,
}) {
  const token = csrfToken();

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', importUrl);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    if (token) {
      xhr.setRequestHeader('X-CSRF-TOKEN', token);
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable || typeof onProgress !== 'function') {
        return;
      }

      onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    });

    xhr.addEventListener('load', () => {
      let json = {};
      try {
        json = JSON.parse(xhr.responseText || '{}');
      } catch {
        json = {};
      }

      if (xhr.status >= 200 && xhr.status < 300 && json.success) {
        resolve(json);
        return;
      }

      reject(new Error(json.error || json.message || 'ذخیره ویدیو در لیست ناموفق بود.'));
    });

    xhr.addEventListener('error', () => {
      reject(new Error('خطا در ارتباط با سرور'));
    });

    xhr.send(JSON.stringify({
      video_id: videoId,
      game_id: meta.gameId,
      game_uuid: meta.gameUuid,
      stream_title: meta.streamTitle,
      duration_seconds: meta.durationSeconds > 0 ? meta.durationSeconds : null,
    }));
  });
}

export function initStreamVodSaver({
  page,
  wrapId = 'gv-top-player-wrap',
  getActiveVideoId,
  streamApi,
} = {}) {
  if (!page || page.dataset.streamVodSaver !== '1') {
    return () => {};
  }

  const uploadUrl = page.dataset.streamRecordingUpload?.trim();
  const importUrl = page.dataset.streamVodImport?.trim();
  const adminUrl = page.dataset.streamRecordingsAdmin?.trim();

  if (!uploadUrl || !importUrl || typeof getActiveVideoId !== 'function' || !streamApi) {
    return () => {};
  }

  const wrap = document.getElementById(wrapId);
  const videoShell = wrap?.querySelector('.product-live-streams__video-shell');

  if (!wrap || !videoShell || videoShell.querySelector('.product-live-streams__vod-save-btn')) {
    return () => {};
  }

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.className = 'product-live-streams__ctrl-btn product-live-streams__vod-save-btn';
  saveBtn.setAttribute('aria-label', 'ذخیره ویدیو در لیست');
  saveBtn.title = 'دانلود و ذخیره در لیست ضبط‌ها';
  saveBtn.textContent = '⬇';
  saveBtn.hidden = true;

  videoShell.appendChild(saveBtn);

  const savePanel = document.createElement('div');
  savePanel.className = 'stream-recording-save-panel';
  savePanel.hidden = true;
  savePanel.innerHTML =
    '<div class="stream-recording-save-panel__dialog" role="dialog" aria-modal="true" aria-labelledby="stream-vod-save-title">'
    + '<p class="stream-recording-save-panel__title" id="stream-vod-save-title">در حال ذخیره ویدیو</p>'
    + '<p class="stream-recording-save-panel__meta" data-vod-save-meta></p>'
    + '<div class="stream-recording-save-panel__progress-wrap">'
    + '<div class="stream-recording-save-panel__progress-track">'
    + '<div class="stream-recording-save-panel__progress-bar" style="width:0%"></div>'
    + '</div>'
    + '<span class="stream-recording-save-panel__progress-label">در حال دانلود… ۰٪</span>'
    + '</div>'
    + '</div>';
  wrap.appendChild(savePanel);

  const saveMetaEl = savePanel.querySelector('[data-vod-save-meta]');
  const saveProgressBar = savePanel.querySelector('.stream-recording-save-panel__progress-bar');
  const saveProgressLabel = savePanel.querySelector('.stream-recording-save-panel__progress-label');

  let saving = false;

  const setSavePanelOpen = (open) => {
    savePanel.hidden = !open;
    savePanel.setAttribute('aria-hidden', open ? 'false' : 'true');
  };

  const setProgress = (percent, label) => {
    const safe = Math.max(0, Math.min(100, Number(percent) || 0));
    saveProgressBar.style.width = `${safe}%`;
    saveProgressLabel.textContent = label || `در حال دانلود… ${safe}٪`;
  };

  const syncSaveButton = () => {
    const videoId = getActiveVideoId();
    const isOpen = wrap.classList.contains('is-open');
    saveBtn.hidden = !isOpen || !videoId;
    saveBtn.disabled = saving;
  };

  const persistVod = async (videoId) => {
    const meta = getVodCardMeta(videoId, page);
    saving = true;
    syncSaveButton();
    setSavePanelOpen(true);
    saveMetaEl.textContent = meta.streamTitle || `ویدیو ${videoId}`;
    setProgress(0, 'در حال آماده‌سازی…');

    try {
      const source = await fetchStreamSource(streamApi, videoId);
      const streamUrl = String(source.stream_url || '');
      const streamType = String(source.stream_type || 'hls');

      if (isDirectMp4Source(streamType, streamUrl)) {
        setProgress(5, 'در حال دانلود مستقیم…');
        const blob = await downloadBlobWithProgress(streamUrl, (percent) => {
          setProgress(percent, `در حال دانلود… ${percent}٪`);
        });

        saveMetaEl.textContent = `${meta.streamTitle || `ویدیو ${videoId}`} · ${formatRecordingSize(blob.size)}`;
        setProgress(100, 'در حال آپلود…');

        const result = await uploadRecording({
          blob,
          mimeType: blob.type || 'video/mp4',
          channel: videoId,
          page,
          startedAt: new Date(),
          durationSeconds: meta.durationSeconds || Number(source.duration_seconds) || 1,
          uploadUrl,
          onProgress: (percent) => {
            setProgress(percent, percent >= 100 ? 'در حال نهایی‌سازی…' : `در حال آپلود… ${percent}٪`);
          },
          metaResolver: () => meta,
        });

        setSavePanelOpen(false);
        const savedUrl = result?.data?.admin_url || adminUrl;
        showToast(
          savedUrl
            ? `ویدیو در لیست ذخیره شد. <a href="${savedUrl}" target="_blank" rel="noopener">مشاهده در پنل</a>`
            : 'ویدیو در لیست ضبط‌ها ذخیره شد.',
          'success',
        );
        return;
      }

      setProgress(10, 'در حال دانلود از Twitch… ممکن است چند دقیقه طول بکشد');
      const result = await importVodOnServer({
        importUrl,
        videoId,
        page,
        meta,
        onProgress: (percent) => {
          setProgress(Math.max(10, percent), percent >= 100 ? 'در حال نهایی‌سازی…' : `در حال دانلود… ${percent}٪`);
        },
      });

      setSavePanelOpen(false);
      const savedUrl = result?.data?.admin_url || adminUrl;
      showToast(
        savedUrl
          ? `ویدیو در لیست ذخیره شد. <a href="${savedUrl}" target="_blank" rel="noopener">مشاهده در پنل</a>`
          : 'ویدیو در لیست ضبط‌ها ذخیره شد.',
        'success',
      );
    } catch (error) {
      setSavePanelOpen(false);
      showToast(error?.message || 'ذخیره ویدیو ناموفق بود.', 'error');
    } finally {
      saving = false;
      syncSaveButton();
    }
  };

  saveBtn.addEventListener('click', async () => {
    if (saving) {
      return;
    }

    const videoId = getActiveVideoId();
    if (!videoId) {
      showToast('ابتدا یک ویدیو را انتخاب کنید.', 'warning');
      return;
    }

    const meta = getVodCardMeta(videoId, page);
    const durationLabel = meta.durationSeconds > 0
      ? formatRecordingDuration(meta.durationSeconds)
      : null;

    if (durationLabel) {
      saveMetaEl.textContent = `${meta.streamTitle || `ویدیو ${videoId}`} · ${durationLabel}`;
    }

    await persistVod(videoId);
  });

  const pollTimer = window.setInterval(syncSaveButton, 400);
  wrap.addEventListener('transitionend', syncSaveButton);

  syncSaveButton();

  return () => {
    window.clearInterval(pollTimer);
    saveBtn.remove();
    savePanel.remove();
    saving = false;
  };
}
